<template>
  <div class="tw-pointer-events-none tw-fixed tw-right-4 tw-top-4 tw-z-[9999] tw-flex tw-w-[min(420px,calc(100vw-2rem))] tw-flex-col tw-gap-2">
    <div
      v-for="t in toastStore.toasts"
      :key="t.id"
      class="tw-pointer-events-auto tw-relative tw-flex tw-items-center tw-justify-between tw-gap-3 tw-overflow-hidden tw-rounded-xl tw-border tw-bg-white/70 tw-backdrop-blur tw-px-4 tw-py-3 tw-shadow-lg tw-shadow-slate-900/10"
      :class="variantClass(t.variant)"
    >
      <div class="tw-min-w-0 tw-flex-1 tw-text-left tw-text-sm tw-font-medium tw-text-slate-900 tw-leading-5">
        {{ t.message }}
      </div>
      <button
        type="button"
        class="tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-text-slate-600 hover:tw-bg-white"
        @click="clearToast(t.id)"
      >
        <font-awesome-icon icon="xmark" class="tw-h-3.5 tw-w-3.5" />
      </button>

      <div
        v-if="t.timeoutMs > 0"
        class="tw-absolute tw-bottom-0 tw-left-0 tw-h-[3px] tw-w-full tw-bg-slate-900/5"
      >
        <div
          class="tw-h-full tw-transition-[width] tw-duration-75 tw-ease-linear"
          :class="progressBarClass(t.variant)"
          :style="{ width: `${progressPercent(t)}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
import { clearToast, toastStore, type ToastItem, type ToastVariant } from '@/lib/toast';

export default defineComponent({
  name: 'ToastHost',
  setup() {
    const now = ref(Date.now());
    let rafId: number | null = null;

    function tick() {
      now.value = Date.now();
      rafId = window.requestAnimationFrame(tick);
    }

    onMounted(() => {
      rafId = window.requestAnimationFrame(tick);
    });

    onBeforeUnmount(() => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = null;
    });

    function variantClass(variant: ToastVariant): string {
      if (variant === 'success') return 'tw-border-emerald-200';
      if (variant === 'error') return 'tw-border-rose-200';
      if (variant === 'warning') return 'tw-border-amber-200';
      return 'tw-border-slate-200';
    }

    function progressBarClass(variant: ToastVariant): string {
      if (variant === 'success') return 'tw-bg-emerald-500/70';
      if (variant === 'error') return 'tw-bg-rose-500/70';
      if (variant === 'warning') return 'tw-bg-amber-500/70';
      return 'tw-bg-slate-600/60';
    }

    function progressPercent(t: ToastItem): number {
      if (!t || t.timeoutMs <= 0) return 0;
      const elapsed = Math.max(0, now.value - (t.createdAt || 0));
      const remaining = Math.max(0, t.timeoutMs - elapsed);
      return (remaining / t.timeoutMs) * 100;
    }

    return {
      toastStore,
      clearToast,
      variantClass,
      progressBarClass,
      progressPercent,
    };
  },
});
</script>
